import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { useSingleMinigrid, useLiveSnapshot } from "../../../hook";
import useWeather from "../../../../../common/hooks/useWeather";
import momiAPI from "../../../../momi/service/momiAPI";
import minigridAPI from "../../../service/minigridAPI";
import { useFilter } from "../../../../filter/hook";
import { LiveEnergySegment } from "../../../liveEnergyDashboardConfig";
import LiveDashboardView from "./LiveDashboardView/LiveDashboardView";

export default function LiveDashboardPresenter() {
    const navigate = useNavigate();
    const { minigridId } = useParams<{ minigridId: string }>();
    const {
        singleMinigridQuery: { data: minigrid },
    } = useSingleMinigrid();

    const { snapshot, isInitialLoading, error: liveError } = useLiveSnapshot();
    const { weather, error: weatherError } = useWeather(
        minigrid?.minigrid_location_lat,
        minigrid?.minigrid_location_lng
    );

    const momiId = minigrid?.minigrid_momi_id?.trim() || "";
    const momiQuery = useQuery({
        queryKey: ["momi-for-live-dashboard", momiId],
        queryFn: () => momiAPI.getSingleMomi(momiId),
        enabled: Boolean(momiId),
    });
    const momiUuid = momiQuery.data?.momi_uuid?.trim() || "";

    const architectureQuery = useQuery({
        queryKey: ["minigrid-architecture", minigridId],
        queryFn: () => minigridAPI.getMinigridArchitecture(minigridId!),
        enabled: Boolean(minigridId),
        staleTime: 5 * 60 * 1000,
    });

    const { filter, setFilter } = useFilter();

    useEffect(() => {
        const now = new Date();
        const start = new Date(now);
        start.setUTCHours(start.getUTCHours() - 24);
        const stop = new Date(
            Date.UTC(
                now.getUTCFullYear(),
                now.getUTCMonth(),
                now.getUTCDate() + 1,
                0,
                0,
                0,
                0
            )
        );
        setFilter({
            ...filter,
            startDate: start.toISOString(),
            stopDate: stop.toISOString(),
            aggregateWindow: "1h",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onEnergyNodeNavigate = (segment: LiveEnergySegment) => {
        if (!minigridId) return;
        navigate(`/main/minigrid/${minigridId}/live/${segment}`);
    };

    return (
        <LiveDashboardView
            snapshot={snapshot}
            weather={weather}
            weatherError={weatherError}
            liveError={liveError}
            isInitialLoading={isInitialLoading}
            onEnergyNodeNavigate={onEnergyNodeNavigate}
            momiUuid={momiUuid}
            filter={filter}
            architecture={architectureQuery.data ?? null}
            architectureLoading={architectureQuery.isLoading}
            architectureError={
                architectureQuery.isError
                    ? "Impossible de récupérer l'architecture"
                    : null
            }
        />
    );
}
